'use client';
import { useMemo, useCallback } from 'react';
import CustomDatePicker from '@/components/ui/CustomDatePicker';
import PhoneNumberInput from '@/components/ui/PhoneNumberInput';
import Select from '@/components/ui/Select';
import { splitPhoneNumber } from '@/utils/phoneParse';
import AddressSearchInput from '@/components/ui/AddressSearchInput';
import StreetSearchInput from '@/components/ui/StreetSearchInput';
import BuildingSearchInput from '@/components/ui/BuildingSearchInput';
import RoomSearchInput from '@/components/ui/RoomSearchInput';
import MunicipalityDropdown from '@/components/ui/MunicipalityDropdown';
import { useLanguage } from '@/context/LanguageContext';
import { checkSettlementHasStreets } from '@/utils/addressApi';
import {
  getConstructionTypeOptions,
  getPcaBuildingTypeOptions,
} from '@/lib/calculatorI18n';
import styles from '../Step1BasicData.module.css';
import AccountEmailInput from '@/components/calculator/AccountEmailInput';

export default function PCAFormFields({
  formData,
  setFormData,
  validationErrors,
  showValidationErrors,
  handlePolicyholderPersonalCodeChange,
  handlePolicyholderAddressChange,
  handleConstructionAddressChange,
  handleConstructionTypeChange,
  handleBuildingTypeChange,
  handleWorkValueChange,
  handleTechnicalSupervisorCertificateChange,
  hidePolicyholder = false,
  constructionAddressOnly = false,
  setValidationErrors,
  allowPastStartDate = false,
}) {
  const { t } = useLanguage();
  const constructionTypeOpts = useMemo(
    () => getConstructionTypeOptions(t),
    [t]
  );
  const buildingTypeOpts = useMemo(() => getPcaBuildingTypeOptions(t), [t]);

  const { countryCode: phoneCountryCode, phone: phoneNational } = useMemo(
    () => splitPhoneNumber(formData.policyholder?.phone_number),
    [formData.policyholder?.phone_number]
  );

  const updatePolicyholderPhone = (countryCode, nationalDigits) => {
    const codeDigits = (countryCode || '+370').replace(/\D/g, '');
    let digits = String(nationalDigits || '').replace(/\D/g, '').slice(0, 8);
    if (digits.startsWith(codeDigits) && digits.length > codeDigits.length) {
      digits = digits.slice(codeDigits.length).slice(0, 8);
    }
    const phone_number = digits ? `+${codeDigits}${digits}` : '';
    setFormData(prev => ({
      ...prev,
      policyholder: {
        ...prev.policyholder,
        phone_number,
      },
    }));
  };

  const address = constructionAddressOnly
    ? formData.construction_address || {}
    : {
        ...formData.policyholder?.address,
        ...formData.construction_address,
      };

  const applyAddressPatch = useCallback(
    patch => {
      setFormData(prev => {
        const nextConstruction = {
          ...prev.construction_address,
          ...patch,
        };
        if (constructionAddressOnly) {
          return { ...prev, construction_address: nextConstruction };
        }
        const policyholderAddressPatch = {};
        [
          'municipality',
          'city',
          'street',
          'house_number',
          'apartment_number',
        ].forEach(field => {
          if (field in patch) {
            policyholderAddressPatch[field] = patch[field];
          }
        });
        return {
          ...prev,
          construction_address: nextConstruction,
          policyholder: {
            ...prev.policyholder,
            address: {
              ...prev.policyholder?.address,
              ...policyholderAddressPatch,
            },
          },
        };
      });

      if (setValidationErrors) {
        setValidationErrors(prev => {
          const next = { ...prev };
          Object.keys(patch).forEach(field => {
            if (field === 'street' || field === 'street_code') return;
            delete next[`construction_${field}`];
            delete next[`policyholder_${field}`];
          });
          if ('settlement_code' in patch) {
            delete next.construction_city;
            delete next.policyholder_city;
          }
          if ('municipality' in patch || 'municipality_code' in patch) {
            delete next.policyholder_municipality;
          }
          if (patch.street_code) {
            delete next.policyholder_street;
            delete next.construction_street;
          } else if ('street' in patch && !String(patch.street || '').trim()) {
            delete next.policyholder_street;
            delete next.construction_street;
          }
          return next;
        });
      }
    },
    [setFormData, setValidationErrors, constructionAddressOnly]
  );

  const patchConstructionAddress = (field, value) => {
    applyAddressPatch({ [field]: value });
  };

  const handleAddressStreetCheck = useCallback(() => {
    const settlementCode = address.settlement_code;
    const currentHasStreets = address.settlementHasStreets;
    if (!settlementCode || currentHasStreets !== undefined) return;

    applyAddressPatch({ settlementHasStreets: null });

    checkSettlementHasStreets(settlementCode)
      .then(hasStreets => {
        applyAddressPatch({ settlementHasStreets: hasStreets });
        if (!hasStreets && setValidationErrors) {
          setValidationErrors(prev => {
            const next = { ...prev };
            delete next.policyholder_street;
            delete next.construction_street;
            return next;
          });
        }
      })
      .catch(error => {
        console.error('[PCAFormFields] Settlement street check failed:', error);
        applyAddressPatch({ settlementHasStreets: true });
      });
  }, [
    address.settlement_code,
    address.settlementHasStreets,
    applyAddressPatch,
    setValidationErrors,
  ]);

  const addressRowStyle = {
    display: 'flex',
    flexDirection: 'row',
    gap: '20px',
    flexWrap: 'wrap',
    width: '100%',
  };

  const addressFieldStyle = { flex: 1, minWidth: '200px' };

  return (
    <>
      {!hidePolicyholder ? (
        <>
      <div
        className={styles.formGroup}
        style={{ width: '100%', maxWidth: '100%' }}
      >
        <label
          className={styles.label}
          style={{ fontSize: '18px', fontWeight: '500', marginBottom: '20px' }}
        >
          {t('calculator.forms.policyholder_section')}
        </label>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.code_label')}</label>
        <input
          className={`${styles.input} ${validationErrors.policyholder_personal_code ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.code_placeholder')}
          value={formData.policyholder?.personal_code || ''}
          autoComplete='username'
          onChange={handlePolicyholderPersonalCodeChange}
          maxLength={12}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_personal_code ? styles.errorHighlight : ''}`}
        >
          {validationErrors.policyholder_personal_code || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.forms.first_name')}
        </label>
        <input
          className={`${styles.input} ${validationErrors.policyholder_first_name ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.first_name_placeholder')}
          value={formData.policyholder?.first_name || ''}
          autoComplete='given-name'
          onChange={e => {
            setFormData(prev => ({
              ...prev,
              policyholder: {
                ...prev.policyholder,
                first_name: e.target.value,
              },
            }));
          }}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_first_name ? styles.errorHighlight : ''}`}
        >
          {validationErrors.policyholder_first_name || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.forms.last_name')}
        </label>
        <input
          className={`${styles.input} ${validationErrors.policyholder_last_name ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.last_name_placeholder')}
          value={formData.policyholder?.last_name || ''}
          autoComplete='family-name'
          onChange={e => {
            setFormData(prev => ({
              ...prev,
              policyholder: {
                ...prev.policyholder,
                last_name: e.target.value,
              },
            }));
          }}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_last_name ? styles.errorHighlight : ''}`}
        >
          {validationErrors.policyholder_last_name || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.forms.email')}
        </label>
        <AccountEmailInput
          className={`${styles.input} ${validationErrors.policyholder_email ? styles.inputError : ''}`}
          placeholder={t('calculator.forms.email_placeholder')}
          value={formData.policyholder?.email || ''}
          onChange={e => {
            setFormData(prev => ({
              ...prev,
              policyholder: {
                ...prev.policyholder,
                email: e.target.value,
              },
            }));
          }}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_email ? styles.errorHighlight : ''}`}
        >
          {validationErrors.policyholder_email || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.forms.phone')}
        </label>
        <PhoneNumberInput
          countryCode={phoneCountryCode}
          onCountryCodeChange={code => updatePolicyholderPhone(code, phoneNational)}
          value={phoneNational}
          onChange={digits => updatePolicyholderPhone(phoneCountryCode, digits)}
          wrapperClassName={`${styles.inputWithIcon} ${validationErrors.policyholder_phone_number ? styles.inputError : ''}`}
          inputClassName={styles.input}
          placeholder={t('account.details.placeholder_phone')}
          autoComplete='tel-national'
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_phone_number ? styles.errorHighlight : ''}`}
        >
          {validationErrors.policyholder_phone_number || ''}
        </div>
      </div>

        </>
      ) : null}

      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.forms.work_start_date')}
        </label>
        <CustomDatePicker
          title={t('calculator.forms.work_start_date')}
          value={formData.startDate}
          onChange={formatted => {
            setFormData(prev => {
              const next = { ...prev, startDate: formatted };
              if (
                formatted &&
                prev.endDate &&
                prev.endDate < formatted
              ) {
                next.endDate = '';
              }
              return next;
            });
          }}
          minDate={(() => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (allowPastStartDate) {
              const min = new Date(today);
              min.setFullYear(min.getFullYear() - 10);
              return min;
            }
            return today;
          })()}
          maxDate={(() => {
            const maxDate = new Date();
            maxDate.setMonth(maxDate.getMonth() + 2);
            maxDate.setHours(23, 59, 59, 999);
            return maxDate;
          })()}
          error={!!validationErrors.startDate}
        />
        <div
          key={`date-error-${(validationErrors.startDate || '').length}`}
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.startDate ? styles.errorHighlight : ''}`}
        >
          {validationErrors.startDate || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.work_end_date')}</label>
        <CustomDatePicker
          title={t('calculator.forms.work_end_date')}
          value={formData.endDate || ''}
          onChange={formatted => {
            setFormData({ ...formData, endDate: formatted });
          }}
          minDate={(() => {
            const base = formData.startDate
              ? new Date(formData.startDate + 'T12:00:00')
              : new Date();
            base.setHours(0, 0, 0, 0);
            return base;
          })()}
          maxDate={(() => {
            const start = formData.startDate
              ? new Date(formData.startDate + 'T12:00:00')
              : new Date();
            const maxDate = new Date(start);
            maxDate.setFullYear(maxDate.getFullYear() + 5);
            maxDate.setHours(23, 59, 59, 999);
            return maxDate;
          })()}
          error={!!validationErrors.endDate}
        />
        <div
          key={`end-date-error-${(validationErrors.endDate || '').length}`}
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.endDate ? styles.errorHighlight : ''}`}
        >
          {validationErrors.endDate || ''}
        </div>
      </div>

      <div
        className={styles.formGroup}
        style={{ width: '100%', maxWidth: '100%' }}
      >
        <label
          className={styles.label}
          style={{
            fontSize: '18px',
            fontWeight: '500',
            marginBottom: '20px',
            marginTop: '20px',
          }}
        >
          {constructionAddressOnly
            ? t('calculator.forms.construction_address')
            : t('calculator.forms.insurance_object_address')}
        </label>
      </div>

      <div style={addressRowStyle}>
        <div className={styles.formGroup} style={addressFieldStyle}>
          <label className={styles.label}>
            {t('calculator.step6.municipality')}
          </label>
          <MunicipalityDropdown
            value={address.municipality_code || ''}
            displayValue={address.municipality || ''}
            onChange={val => {
              if (!val) {
                applyAddressPatch({
                  municipality_code: '',
                  municipality: '',
                  settlement_code: '',
                  settlementHasStreets: undefined,
                  street_code: '',
                  building_code: '',
                  room_code: '',
                  city: '',
                  street: '',
                  house_number: '',
                  apartment_number: '',
                });
              }
            }}
            onMunicipalitySelect={municipality => {
              const municipalityName =
                municipality?.name || municipality?.name_normalized || '';
              applyAddressPatch({
                municipality_code: municipality?.code || '',
                municipality: municipalityName,
                settlement_code: '',
                settlementHasStreets: undefined,
                street_code: '',
                building_code: '',
                room_code: '',
                city: '',
                street: '',
                house_number: '',
                apartment_number: '',
              });
            }}
            placeholder={t('calculator.step6.placeholder_select_municipality')}
            error={!!validationErrors.policyholder_municipality}
            className={
              validationErrors.policyholder_municipality ? styles.inputError : ''
            }
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_municipality ? styles.errorHighlight : ''}`}
          >
            {validationErrors.policyholder_municipality || ''}
          </div>
        </div>
        <div className={styles.formGroup} style={addressFieldStyle}>
          <label className={styles.label}>{t('calculator.forms.city')}</label>
          <AddressSearchInput
            value={address.settlement_code || ''}
            displayValue={address.city || ''}
            municipalityCode={address.municipality_code || null}
            displayOnlyCityName={true}
            onChange={settlementCode => {
              if (settlementCode === '') {
                applyAddressPatch({
                  settlement_code: '',
                  settlementHasStreets: undefined,
                  street_code: '',
                  building_code: '',
                  room_code: '',
                  city: '',
                  street: '',
                  house_number: '',
                  apartment_number: '',
                });
              } else {
                patchConstructionAddress('settlement_code', settlementCode);
              }
            }}
            onSettlementSelect={settlement => {
              applyAddressPatch({
                settlement_code: settlement.code,
                municipality_code:
                  settlement.municipality_code || address.municipality_code || '',
                municipality:
                  settlement.municipality_name || address.municipality || '',
                settlementHasStreets: undefined,
                street_code: '',
                building_code: '',
                room_code: '',
                city: settlement.name || '',
                street: '',
                house_number: '',
                apartment_number: '',
              });
            }}
            placeholder={
              address.municipality_code
                ? t('calculator.forms.city_placeholder')
                : 'Pirmiausia pasirinkite savivaldybę'
            }
            error={
              !!validationErrors.policyholder_city ||
              !!validationErrors.construction_city
            }
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && (validationErrors.policyholder_city || validationErrors.construction_city) ? styles.errorHighlight : ''}`}
          >
            {validationErrors.policyholder_city ||
              validationErrors.construction_city ||
              ''}
          </div>
        </div>
      </div>

      <div style={addressRowStyle}>
        <div className={styles.formGroup} style={addressFieldStyle}>
          <label className={styles.label}>{t('calculator.forms.street')}</label>
          <StreetSearchInput
            value={address.street_code || ''}
            displayValue={address.street || ''}
            settlementCode={address.settlement_code || null}
            hasStreets={address.settlementHasStreets}
            noStreetsPlaceholder={t('calculator.step6.placeholder_no_streets')}
            checkingPlaceholder={t('calculator.step6.placeholder_checking_streets')}
            onActivate={handleAddressStreetCheck}
            onChange={streetCode => {
              if (streetCode === '') {
                applyAddressPatch({
                  street_code: '',
                  building_code: '',
                  room_code: '',
                  street: '',
                  house_number: '',
                  apartment_number: '',
                });
              } else {
                patchConstructionAddress('street_code', streetCode);
              }
            }}
            onQueryChange={text => {
              applyAddressPatch({
                street: text,
                street_code: '',
                building_code: '',
              });
            }}
            onUnmatched={() => {
              if (!setValidationErrors) return;
              const message = t(
                'calculator.forms.validation.street_select_from_list'
              );
              setValidationErrors(prev => ({
                ...prev,
                policyholder_street: message,
                construction_street: message,
              }));
            }}
            onStreetSelect={street => {
              applyAddressPatch({
                street_code: street.code,
                street: street.name,
                building_code: '',
                room_code: '',
                house_number: '',
                apartment_number: '',
              });
            }}
            placeholder={
              address.settlement_code
                ? t('calculator.forms.street_placeholder')
                : t('calculator.step6.placeholder_select_city_first')
            }
            error={
              !!validationErrors.policyholder_street ||
              !!validationErrors.construction_street
            }
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && (validationErrors.policyholder_street || validationErrors.construction_street) ? styles.errorHighlight : ''}`}
          >
            {validationErrors.policyholder_street ||
              validationErrors.construction_street ||
              ''}
          </div>
        </div>
        <div className={styles.formGroup} style={addressFieldStyle}>
          <label className={styles.label}>{t('calculator.forms.house_number')}</label>
          <BuildingSearchInput
            value={address.building_code || ''}
            displayValue={address.house_number || ''}
            streetCode={
              address.settlementHasStreets === false
                ? null
                : address.street_code || null
            }
            onActivate={handleAddressStreetCheck}
            onChange={buildingCode => {
              if (buildingCode === '') {
                applyAddressPatch({
                  building_code: '',
                  room_code: '',
                  house_number: '',
                  apartment_number: '',
                });
              } else {
                patchConstructionAddress('building_code', buildingCode);
              }
            }}
            onBuildingSelect={building => {
              const houseNumber =
                building.full_number || building.building_number;
              applyAddressPatch({
                building_code: building.code,
                house_number: houseNumber,
                room_code: '',
                apartment_number: '',
              });
            }}
            placeholder={t('calculator.forms.house_placeholder')}
            error={
              !!validationErrors.policyholder_house_number ||
              !!validationErrors.construction_house_number
            }
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && (validationErrors.policyholder_house_number || validationErrors.construction_house_number) ? styles.errorHighlight : ''}`}
          >
            {validationErrors.policyholder_house_number ||
              validationErrors.construction_house_number ||
              ''}
          </div>
        </div>
        <div className={styles.formGroup} style={addressFieldStyle}>
          <label className={styles.label}>
            {t('calculator.forms.apartment_number')}
          </label>
          <RoomSearchInput
            value={address.room_code || ''}
            displayValue={address.apartment_number || ''}
            buildingCode={address.building_code || null}
            onChange={roomCode => {
              if (roomCode === '') {
                applyAddressPatch({
                  room_code: '',
                  apartment_number: '',
                });
              } else {
                patchConstructionAddress('room_code', roomCode);
              }
            }}
            onRoomSelect={room => {
              applyAddressPatch({
                room_code: room.code,
                apartment_number: room.room_number,
              });
            }}
            placeholder={t('calculator.forms.apartment_placeholder')}
            error={
              !!validationErrors.policyholder_apartment_number ||
              !!validationErrors.construction_apartment_number
            }
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && (validationErrors.policyholder_apartment_number || validationErrors.construction_apartment_number) ? styles.errorHighlight : ''}`}
          >
            {validationErrors.policyholder_apartment_number ||
              validationErrors.construction_apartment_number ||
              ''}
          </div>
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.forms.field_construction_type')}
        </label>
        <Select
          options={constructionTypeOpts}
          value={formData.construction_type || ''}
          onChange={handleConstructionTypeChange}
          placeholder={t('calculator.forms.construction_type_placeholder')}
          className={
            validationErrors.construction_type ? styles.selectError : ''
          }
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.construction_type ? styles.errorHighlight : ''}`}
        >
          {validationErrors.construction_type || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.forms.field_building_type')}
        </label>
        <Select
          options={buildingTypeOpts}
          value={formData.building_type || ''}
          onChange={handleBuildingTypeChange}
          placeholder={t('calculator.forms.building_type_placeholder')}
          className={validationErrors.building_type ? styles.selectError : ''}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.building_type ? styles.errorHighlight : ''}`}
        >
          {validationErrors.building_type || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.forms.technical_supervisor_cert')}
        </label>
        <input
          className={`${styles.input} ${validationErrors.technical_supervisor_certificate_number ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.technical_supervisor_cert_placeholder')}
          value={formData.technical_supervisor_certificate_number || ''}
          onChange={handleTechnicalSupervisorCertificateChange}
          maxLength={128}
          autoComplete='off'
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.technical_supervisor_certificate_number ? styles.errorHighlight : ''}`}
        >
          {validationErrors.technical_supervisor_certificate_number || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.work_value_eur')}</label>
        <input
          className={`${styles.input} ${validationErrors.work_value ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.work_value_placeholder')}
          value={formData.work_value || ''}
          onChange={handleWorkValueChange}
          maxLength={8}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.work_value ? styles.errorHighlight : ''}`}
        >
          {validationErrors.work_value || ''}
        </div>
      </div>
    </>
  );
}
